import { onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import axiosInst from '@/utils/axiosinstance';
import { isSeoEnabled } from '@/utils/seoEnv';

/**
 * Composable to dynamically inject meta tags based on page name
 * Usage in any Vue component:
 *   import { useDynamicMetadata } from '@/composables/useDynamicMetadata'
 *   export default {
 *     setup() {
 *       useDynamicMetadata()
 *       return {}
 *     }
 *   }
 */
export function useDynamicMetadata() {
  const route = useRoute();

  /**
   * Fetch metadata from backend API
   */
  const fetchMetadata = async (pageName) => {
    try {
      if (!pageName) return;

      // Backend expects pageName without leading slash in URL param
      const apiPageParam = encodeURIComponent(pageName.replace(/^\/+/, ''));
      const response = await axiosInst.get(`/api/metadata/${apiPageParam}`);

      if (response.data && response.data.data) {
        const { metaTags } = response.data.data;
        injectMetaTags(metaTags);
      }
    } catch (error) {
      console.warn(`Could not fetch metadata for ${pageName}:`, error.message);
      // Silently fail if metadata not found
    }
  };

  /**
   * Inject meta tags into document head
   */
  const injectMetaTags = (metaTags) => {
    if (!metaTags || typeof metaTags !== 'object') return;

    // Remove existing dynamic meta tags added by this system
    const existingMeta = document.querySelectorAll('[data-dynamic-meta="true"]');
    existingMeta.forEach((tag) => tag.remove());

    // Inject new meta tags
    Object.entries(metaTags).forEach(([key, value]) => {
      let element;

      if (key === 'title') {
        document.title = value;
      } else if (key.startsWith('og:') || key.startsWith('twitter:')) {
        // Remove any existing Open Graph/Twitter Card meta with same property
        const existing = document.querySelector(`meta[property="${key}"]`);
        if (existing) existing.remove();
        
        // Create new Open Graph/Twitter Card meta tag
        element = document.createElement('meta');
        element.setAttribute('property', key);
        element.setAttribute('content', value);
        element.setAttribute('data-dynamic-meta', 'true');
        document.head.appendChild(element);
      } else {
        // Remove any existing standard meta with same name
        const existing = document.querySelector(`meta[name="${key}"]`);
        if (existing) existing.remove();
        
        // Create new standard meta tag
        element = document.createElement('meta');
        element.setAttribute('name', key);
        element.setAttribute('content', value);
        element.setAttribute('data-dynamic-meta', 'true');
        document.head.appendChild(element);
      }
    });
  };

  /**
   * Extract page name from route
   */
  const getPageName = () => {
    // Non-production keeps a bare head: no title, no meta. Bailing out here
    // also stops the /api/metadata request, which has no purpose off prod.
    if (!isSeoEnabled()) return null;

    const path = route.path || '/';
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return null; // homepage/root – no metadata

    const first = segments[0].toLowerCase();
    // Skip admin pages and non-public sections
    if (first === 'admin') return null;

    // Only fetch for known public pages to avoid 404 noise
    const whitelist = new Set(['dashboard', 'rentals', 'jobs', 'sale', 'events', 'services', 'contact-us','profile','my-adds','my-pass','messages','view-post']);
    if (!whitelist.has(first)) return null;

    return `/${first}`;
  };

  /**
   * Watch for route changes and update metadata
   */
  watch(
    () => route.path,
    () => {
      const pageName = getPageName();
      if (pageName) fetchMetadata(pageName);
    }
  );

  /**
   * Fetch metadata on component mount
   */
  onMounted(() => {
    const pageName = getPageName();
    if (pageName) fetchMetadata(pageName);
  });

  return {
    fetchMetadata,
    injectMetaTags,
    getPageName,
  };
}
